import axios from 'axios'
import { buildRequestedBy } from './portalOrganizationRequests.js'
import {
  appendInstructorPortalOrderSource,
  instructorPortalOrderSourceFields,
} from './portalOrderSource.js'

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png']
const ACCEPTED_EXT = /\.(pdf|jpe?g|png)$/i

function formatCertDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function normalizeCertStatus(raw) {
  const key = String(raw ?? 'pending').toLowerCase()
  const map = {
    pending: 'Pending',
    approved: 'Active',
    active: 'Active',
    denied: 'Denied',
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
    removed: 'Removed',
    expired: 'Expired',
  }
  return map[key] ?? 'Pending'
}

function certPreviewKind(fileName, mimeType) {
  const name = String(fileName ?? '').toLowerCase()
  const mime = String(mimeType ?? '').toLowerCase()
  if (mime.includes('pdf') || name.endsWith('.pdf')) return 'pdf'
  if (mime === 'image/jpeg' || mime === 'image/png' || /\.(jpe?g|png)$/.test(name)) return 'image'
  return 'unknown'
}

/**
 * Resolve a display URL for inline preview (PDF via proxy when remote).
 * @param {{ url: string, kind: string, isBlob?: boolean }} previewSource
 */
export async function fetchTaxExemptCertDisplayUrl(previewSource) {
  if (!previewSource?.url) return null
  const { url, kind, isBlob } = previewSource
  if (kind === 'image') return url
  if (kind !== 'pdf') return null
  if (isBlob) return url
  const { data } = await axios.get(`${import.meta.env.VITE_BASE_LINK}/proxy-pdf`, {
    params: { url },
    responseType: 'blob',
    withCredentials: true,
  })
  return URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
}

/**
 * @param {Record<string, unknown> | null | undefined} cert
 * @param {File | null | undefined} localFile
 */
export function taxExemptCertPreviewSource(cert, localFile) {
  if (localFile instanceof File) {
    return {
      url: URL.createObjectURL(localFile),
      fileName: localFile.name,
      kind: certPreviewKind(localFile.name, localFile.type),
      isBlob: true,
    }
  }
  const url = cert?.fileUrl ?? cert?.downloadUrl ?? null
  if (!url) return null
  const fileName = String(cert?.name ?? 'certificate')
  return {
    url: String(url),
    fileName,
    kind: certPreviewKind(fileName, cert?.mimeType ?? cert?.contentType),
    isBlob: false,
  }
}

/**
 * @param {Record<string, unknown>} row
 */
export function mapTaxExemptCertFromApi(row) {
  const id = row.id ?? row._id ?? row.certId
  const status = normalizeCertStatus(row.status)
  const submittedBy = row.submittedBy
  const submitterName =
    (typeof submittedBy === 'object' && submittedBy !== null
      ? submittedBy.name
      : row.submittedByName ?? row.submittedBy) ?? ''
  const submitterEmail =
    (typeof submittedBy === 'object' && submittedBy !== null
      ? submittedBy.email
      : row.submittedEmail) ?? ''

  return {
    id,
    name: String(row.fileName ?? row.name ?? 'Certificate'),
    date: formatCertDate(row.createdAt ?? row.uploadedAt ?? row.date),
    status,
    expires: formatCertDate(row.expiresAt ?? row.expires),
    submittedBy: String(submitterName),
    submittedEmail: String(submitterEmail),
    responded: status !== 'Pending',
    respondedAt: row.respondedAt ?? row.responseDate ?? null,
    responseDate: formatCertDate(row.respondedAt ?? row.responseDate ?? null) || null,
    hasViewed:
      row.hasViewed === true ||
      row.viewed === true ||
      !!row.viewedAt ||
      !!row.responseViewedAt,
    fileUrl: row.fileUrl ?? row.downloadUrl ?? null,
    expiresAt: row.expiresAt ?? null,
    createdAt: row.createdAt ?? null,
  }
}

/**
 * @param {string} organizationId
 */
export async function fetchOrganizationTaxExemptCerts(organizationId) {
  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/get-organization-tax-exempt-certificates`,
    { organizationId },
    {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    }
  )
  const rows = data?.certificates ?? data?.taxExemptCerts ?? data?.requests ?? data ?? []
  return (Array.isArray(rows) ? rows : []).map(mapTaxExemptCertFromApi)
}

/**
 * @param {File} file
 */
export function validateTaxExemptCertFile(file) {
  if (!file) return 'Please select a certificate file.'
  if (file.size > 15 * 1024 * 1024) return 'File must be 15 MB or smaller.'
  const typeOk = ACCEPTED_TYPES.includes(file.type) || ACCEPTED_EXT.test(file.name)
  if (!typeOk) return 'Only PDF, JPG, or PNG files are accepted.'
  return null
}

/**
 * Upload tax exempt certificate for RefReps review.
 * @param {{
 *   organizationId: string,
 *   file: File,
 *   expiresAt: string,
 *   submittedBy?: { userId?: string, name?: string, email?: string },
 *   replaceCertId?: string | number,
 * }} payload
 */
export async function uploadTaxExemptCertificate(payload) {
  const { organizationId, file, expiresAt, submittedBy, replaceCertId } = payload
  const err = validateTaxExemptCertFile(file)
  if (err) throw new Error(err)

  const form = new FormData()
  form.append('file', file)
  form.append('organizationId', organizationId)
  form.append('expiresAt', expiresAt)
  form.append('submittedBy', JSON.stringify(submittedBy ?? {}))
  if (replaceCertId != null && replaceCertId !== '') {
    form.append('replaceCertId', String(replaceCertId))
  }
  appendInstructorPortalOrderSource(form)

  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/upload-tax-exempt-certificate`,
    form,
    {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  )
  return data
}

/**
 * Update expiration on an existing certificate (no new file).
 * @param {{
 *   organizationId: string,
 *   certId: string | number,
 *   expiresAt: string,
 *   submittedBy?: { userId?: string, name?: string, email?: string },
 * }} payload
 */
export async function updateTaxExemptCertificate(payload) {
  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/update-tax-exempt-certificate`,
    { ...payload, ...instructorPortalOrderSourceFields() },
    {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    }
  )
  return data
}

/**
 * Request removal of an active tax exempt certificate.
 * @param {{
 *   organizationId: string,
 *   certId: string | number,
 *   requestedBy?: { userId?: string, name?: string, email?: string },
 * }} payload
 */
export async function requestRemoveTaxExemptCertificate(payload) {
  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/request-remove-tax-exempt-certificate`,
    { ...payload, ...instructorPortalOrderSourceFields() },
    {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    }
  )
  return data
}

/**
 * @param {import('pinia').Store | Record<string, unknown>} mainStore
 */
export function taxExemptSubmitterFromStore(mainStore) {
  return buildRequestedBy(mainStore)
}

/**
 * @param {Record<string, unknown>} data
 */
export function mergeTaxExemptCertsFromResponse(data, existing = []) {
  const rows = data?.certificate ?? data?.certificates ?? data?.taxExemptCerts
  if (!rows) return null
  const mapped = (Array.isArray(rows) ? rows : [rows]).map(mapTaxExemptCertFromApi)
  const byId = new Map(existing.map((c) => [String(c.id), c]))
  mapped.forEach((c) => byId.set(String(c.id), c))
  return [...byId.values()]
}
